import axiosClient from "../api/axiosClient";

/**
 * Promo Image service — a single account-level social-share preview image
 * (mobile's screen title is literally "Social Album Thumbnail"). Ported from
 * src/Screens/create-promo-media. Note the response bodies use
 * lowercase-first keys (promoImageUrl/thumbnailUrl/message), unlike almost
 * everywhere else in this API.
 */

export const getPromoImage = () => axiosClient.get("/Chat/GalleryAlbum/promo-image-url");

/** @param {{promoImageUrl, thumbnailUrl}} payload */
export const setPromoImage = (payload) =>
  axiosClient.post("/Chat/GalleryAlbum/promo-image-url", JSON.stringify(payload));

export const removePromoImage = () => axiosClient.post("/Chat/GalleryAlbum/promo-image/inactive", {});

const STATIC_THUMBNAIL_WIDTH = 480;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

/** Client-generated thumbnail — same approach as EventImagePicker's GIF static thumbnail. */
const generateThumbnail = async (file) => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const targetW = Math.min(img.width, STATIC_THUMBNAIL_WIDTH);
    const targetH = Math.round(img.height * (targetW / img.width));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH || img.height;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    return blob ? new File([blob], `promo_thumb_${Date.now()}.jpg`, { type: "image/jpeg" }) : null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Upload the original file + a client-generated thumbnail in one Image/Bulk
 * call, mirroring createEventService.uploadGifWithThumbnail's two-file
 * pattern.
 * @param {File} file
 * @returns {Promise<{url: string, thumbnail: string}|null>}
 */
export const uploadPromoImage = async (file) => {
  const thumbnailFile = await generateThumbnail(file);
  if (!thumbnailFile) return null;

  const formData = new FormData();
  formData.append("folderName", "PromoImage");
  formData.append("files", file);
  formData.append("files", thumbnailFile);
  const res = await axiosClient.post("/Image/Bulk", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  const body = res.data;
  const ok = body?.status === "Success" || body?.Status === "Success";
  const result = body?.result ?? body?.Result;
  if (!ok || !Array.isArray(result) || result.length < 2) return null;

  const resolveUrl = (item) => item?.originalUrl ?? item?.OriginalUrl ?? item?.url ?? item?.Url ?? "";
  const url = resolveUrl(result[0]);
  const thumbnail = resolveUrl(result[1]);
  return url && thumbnail ? { url, thumbnail } : null;
};
